import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Rocket, HelpCircle } from 'lucide-react';
import CompanionPanelNotice, { STAGE_TONE } from '@/components/CompanionPanelNotice';

const TREND_ICON = {
  acceleration: Rocket,
  upward: TrendingUp,
  plateau: Minus,
  regression: TrendingDown,
  not_enough_history: HelpCircle,
};

const Delta = ({ delta, testid }) => {
  if (!delta) return null;
  const up = delta.change > 0;
  const flat = delta.change === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${
        flat ? 'text-gray-500' : up ? 'text-emerald-600' : 'text-amber-600'
      }`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      {up ? '+' : ''}{delta.change} since {delta.from_short_label}
    </span>
  );
};

export const ResultBody = ({ result }) => {
  const tone = STAGE_TONE[result.overall_stage] || STAGE_TONE[1];
  const trend = result.trajectory;
  const TrendIcon = TREND_ICON[trend?.trend] || HelpCircle;
  const notMeasured = result.not_measured || [];

  return (
    <div className="space-y-6" data-testid="assessment-result-body">
      {(result.focus_statement || result.narrative) && (
        <Card data-testid="result-focus-card">
          <CardContent className="p-6 space-y-3">
            {result.narrative && (
              <p className="text-sm md:text-base text-gray-700 whitespace-pre-line" data-testid="result-narrative">
                {result.narrative}
              </p>
            )}
            {result.focus_statement && (
              <p className="text-sm text-gray-500" data-testid="result-focus-statement">
                {result.focus_statement}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        <h3 className="text-base md:text-lg font-semibold text-white" data-testid="result-movement-heading">
          How you're moving
        </h3>
        {result.dimensions.map((d) => {
          const dTone = STAGE_TONE[d.stage] || STAGE_TONE[1];
          return (
            <Card key={d.dimension} data-testid={`result-dimension-${d.dimension}`}>
              <CardContent className="p-6">
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <h4 className="text-base md:text-lg font-semibold text-gray-900">{d.dimension_name}</h4>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-gray-900">{d.percentage}%</div>
                    <div className={`text-xs font-semibold ${dTone.text}`}>{d.stage_name}</div>
                  </div>
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {d.since_last
                    ? <Delta delta={d.since_last} testid={`result-delta-${d.dimension}`} />
                    : <span className="text-xs text-gray-400" data-testid={`result-delta-${d.dimension}`}>First reading for this dimension</span>}
                  {d.since_baseline && d.since_baseline.from_short_label !== d.since_last?.from_short_label && (
                    <span className="text-xs text-gray-400">
                      {d.since_baseline.change > 0 ? '+' : ''}{d.since_baseline.change} since Baseline
                    </span>
                  )}
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden mb-4">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${dTone.bar}`}
                    style={{ width: `${d.percentage}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 whitespace-pre-line">{d.interpretation}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {notMeasured.length > 0 && (
        <Card className="bg-gray-50" data-testid="result-not-measured">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Not measured in this one
            </h3>
            <div className="space-y-3">
              {notMeasured.map((d) => (
                <div
                  key={d.dimension}
                  className="flex items-baseline justify-between gap-4 opacity-60"
                  data-testid={`result-carried-${d.dimension}`}
                >
                  <span className="text-sm text-gray-700">{d.dimension_name}</span>
                  <span className="text-xs text-gray-500 text-right shrink-0">
                    {d.has_history
                      ? `Last reading ${d.last_measured_short_label}: ${d.percentage}%, ${d.stage_name}`
                      : 'Not measured yet'}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              These readings still stand — they simply weren't part of this assessment, so they
              haven't changed and they don't affect this score.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className={`border-2 ${tone.border} ${tone.bg}`} data-testid="result-overall-card">
        <CardContent className="p-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            <div>
              <div className="text-4xl font-bold text-gray-900" data-testid="result-overall-score">
                {result.overall_score}
                <span className="text-xl text-gray-500">%</span>
              </div>
              <div className={`text-base md:text-lg font-semibold mt-1 ${tone.text}`} data-testid="result-overall-stage">
                {result.overall_stage_name}
              </div>
              {trend?.comparison && (
                <div className="text-xs text-gray-500 mt-2" data-testid="result-overall-blend-note">
                  Overall {trend.comparison.change > 0 ? '+' : ''}{trend.comparison.change}, blended
                  across the {trend.comparison.dimensions_compared.length} dimension
                  {trend.comparison.dimensions_compared.length === 1 ? '' : 's'} this assessment and
                  the last one both measured
                </div>
              )}
            </div>
            <div className="flex-1 sm:pl-6 sm:border-l border-gray-300">
              <p className="text-sm md:text-base text-gray-700 whitespace-pre-line" data-testid="result-overall-text">
                {result.overall_stage_text}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {trend && (
        <Card data-testid="result-trajectory">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <TrendIcon className="w-6 h-6 text-cyan-600 shrink-0 mt-1" />
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900">
                  Trajectory — {trend.trend_label}
                </h3>
                {trend.comparison ? (
                  <p className="text-sm text-gray-600 mt-1">
                    {trend.comparison.from_score} → {trend.comparison.to_score}
                    {' '}({trend.comparison.change > 0 ? '+' : ''}{trend.comparison.change}) compared
                    with your previous assessment.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 mt-1">
                    Your trajectory appears once you have completed a second assessment.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {result.closing && (
        <Card className="border-2 border-violet-300 bg-violet-50" data-testid="result-closing">
          <CardContent className="p-8">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
              {result.closing.title}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              All six dimensions, from your Baseline to your most recent reading.
            </p>
            <div className="space-y-4">
              {result.closing.dimensions.map((row) => (
                <div key={row.dimension} data-testid={`closing-${row.dimension}`}>
                  <div className="flex items-baseline justify-between gap-4 mb-1">
                    <span className="text-sm font-semibold text-gray-900">{row.dimension_name}</span>
                    <span className="text-sm text-gray-700 shrink-0">
                      {row.from_percentage}% → {row.to_percentage}%
                      <span className={`ml-2 font-semibold ${
                        row.change > 0 ? 'text-emerald-600' : row.change < 0 ? 'text-amber-600' : 'text-gray-500'
                      }`}>
                        {row.change > 0 ? '+' : ''}{row.change}
                      </span>
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.from_stage_name} → {row.to_stage_name}
                    {!row.latest_is_final && ` · last measured at ${row.to_short_label}`}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AssessmentResultPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isFirst = params.get('first') === '1';

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/assessments/my/results/${attemptId}`)
      .then((res) => setResult(res.data))
      .catch(() => setError('That result could not be found.'));
  }, [attemptId]);

  if (error) {
    return <div className="text-center py-20 text-gray-300" data-testid="result-error">{error}</div>;
  }
  if (!result) {
    return <div className="text-center py-20 text-gray-300">Loading your results…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="assessment-result-page">
      <div className="mb-6 flex flex-wrap gap-3">
        <Button variant="outline" className="bg-white" onClick={() => navigate('/dashboard/assessments')} data-testid="result-all-results-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> All my results
        </Button>
        <Link to="/dashboard/modules">
          <Button variant="outline" className="bg-white" data-testid="result-modules-btn">
            Coaching Program
          </Button>
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{result.location_label}</h1>
        <p className="text-gray-300 text-sm">
          Completed {new Date(result.submitted_at).toLocaleDateString()} · This is a reflection of
          where you are, not a score you passed or failed.
        </p>
      </div>

      {isFirst && (
        <div className="mb-6">
          <CompanionPanelNotice />
        </div>
      )}

      <ResultBody result={result} />
    </div>
  );
};

export default AssessmentResultPage;
